import apiInstance from 'services/ApiService';
import { ApiConfig } from 'config/ApiConfig';

/**
 * Adds the toastStatus to the data object.
 * 
 * @param {Object} data - The data object to which the toastStatus should be added.
 * @param {string} toastStatus - The toastStatus to be added.
 * @returns {Object} - The modified data object with toastStatus included.
 */
const appendToastStatus = (data, toastStatus) => {
  return { ...data, toastStatus };
};

/**
 * Service to handle user login.
 * 
 * @param {Object} credentials - The login credentials (username, password, etc.)
 * @returns {Promise} - The API response
 */
export const loginService = async (credentials) => {
  const { url, method, toastStatus } = ApiConfig.AUTH.LOGIN;
  const requestData = appendToastStatus(credentials, toastStatus);
  
  const response = await apiInstance({
    url, 
    method, 
    data: requestData

  });
  
  return response;
};

/**
 * Service to handle user registration.
 * 
 * @param {Object} userData - The user data for registration
 * @returns {Promise} - The API response
 */
export const registerService = async (userData) => {
  const { url, method, toastStatus } = ApiConfig.AUTH.REGISTER;
  const requestData = appendToastStatus(userData, toastStatus);
  
  const response = await apiInstance({
    url, 
    method, 
    data: requestData
  });
  
  return response;
};

/**
 * Service to handle social login.
 * 
 * @param {Object} socialData - The data for social login (e.g., accessToken from Google/Facebook)
 * @returns {Promise} - The API response
 */
export const socialLoginService = async (socialData) => {
  const { url, method, toastStatus } = ApiConfig.AUTH.SOCIAL_LOGIN;
  const requestData = appendToastStatus(socialData, toastStatus);
  
  const response = await apiInstance({
    url, 
    method, 
    data: requestData
  });
  
  return response;
};

/**
 * Service to handle forgot password request.
 * 
 * @param {Object} emailData - The data for the forgot password request, typically including the user's email.
 * @returns {Promise} - The API response
 */
export const forgotPasswordService = async (emailData) => {
  const { url, method, toastStatus } = ApiConfig.AUTH.FORGOT_PASSWORD;
  const requestData = appendToastStatus(emailData, toastStatus);
  
  const response = await apiInstance({
    url, 
    method, 
    data: requestData
  });
  
  return response;
};

/**
/**
 * Reset the user's password.
 *
 * @param {Object} resetPayload - The data for the password reset request, including new password details.
 * @param {Object} query - Query parameters, including the token.
 * @returns {Promise} - The API response
 */
export const resetPasswordService = async (resetPayload, query) => {
  const { url, method, toastStatus } = ApiConfig.AUTH.RESET_PASSWORD;
  const requestData = appendToastStatus(resetPayload, toastStatus);
  
  const response = await apiInstance({
    url,
    method,
    data: requestData,
    params: query // Add query parameters here
  });
  
  return response;
};